/**
 * One source of truth for "show an image, fall back to the 8K Brainzr logo
 * if the image is missing OR fails to load (404, ERR_NAME_NOT_RESOLVED, etc.)".
 *
 * Use this for every artwork rendering — channel logos, movie posters,
 * series covers, episode stills, favourites thumbnails. Anywhere we render
 * an upstream image URL.
 */
import { useEffect, useState } from "react";
import { safeImg } from "../lib/img";
import { Logo } from "./Logo";

interface Props {
  src?: string | null;
  alt: string;
  /** Tailwind classes applied to the rendered <img>. Use this for object-fit + transitions. */
  imgClass?: string;
  /** Tailwind classes applied to the wrapper div (also wraps the placeholder).
   *  Set explicit width/height/aspect on this. */
  className?: string;
  /** Layout the placeholder logo as a row (landscape cards) or stack (portrait). */
  orientation?: "row" | "stack";
}

export function PosterImage({
  src,
  alt,
  imgClass = "w-full h-full object-cover",
  className = "w-full h-full",
  orientation = "stack",
}: Props) {
  const safe = safeImg(src ?? undefined);
  const [ok, setOk] = useState(true);
  useEffect(() => {
    setOk(!!safe);
  }, [safe]);

  if (!safe || !ok) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-surface/40`}
        style={{ containerType: "inline-size" }}
      >
        <Logo orientation={orientation} />
      </div>
    );
  }
  return (
    <img
      loading="lazy"
      src={safe}
      alt={alt}
      className={`${className} ${imgClass}`}
      onError={() => setOk(false)}
    />
  );
}
